import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { smart_meters_base_url } from '../../../../../../utils/urls';
import { toastify } from '../../../../../../utils/toast';

function CommunicationLogsTab({ meterId }) {
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const toast = useRef(null);

    // Pagination options
    const rowsPerPageOptions = [10, 20, 50, 100];

    useEffect(() => {
        if (meterId) {
            fetchLogs();
        }
    }, [meterId]);

    // Add event listener for meter updates to refresh logs
    useEffect(() => {
        const handleMeterUpdate = (event) => {
            if (event.detail.meterId === meterId) {
                fetchLogs();
            }
        };

        window.addEventListener('meter-updated', handleMeterUpdate);
        return () => {
            window.removeEventListener('meter-updated', handleMeterUpdate);
        };
    }, [meterId]);

    useEffect(() => {
        if (searchTerm) {
            const filtered = logs.filter(log =>
                Object.values(log).some(value =>
                    value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
            setFilteredLogs(filtered);
        } else {
            setFilteredLogs(logs);
        }
    }, [searchTerm, logs]);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const url = `${smart_meters_base_url}/get_meter_logs/${meterId}`;
            const response = await makeRequest2(url, 'GET');
            if (response.success) {
                const data = response.data.logs || [];
                setLogs(data);
                setFilteredLogs(data);
            } else {
                toastify('Failed to fetch communication logs', 'error');
            }
        } catch (error) {
            toastify('Error fetching communication logs', 'error');
        } finally {
            setLoading(false);
        }
    };

    const timestampBodyTemplate = (rowData) => {
        return new Date(rowData.timestamp).toLocaleString();
    };

    const actionByBodyTemplate = (rowData) => {
        return rowData.actionBy || 'N/A';
    };

    const reasonBodyTemplate = (rowData) => {
        if (!rowData.reason) return 'N/A';
        
        // Convert underscore values to readable format
        const reasonMap = {
            'vacant_house': 'Vacant House',
            'billing_service_failed_close': 'Billing Service Failed to Close',
            'caretaker_instructed': 'Caretaker Instructed',
            'no_longer_installed': 'No Longer Installed',
            'troubleshooting': 'Troubleshooting',
            'billing_service_failed_open': 'Billing Service Failed to Open',
            'opened_for_repair': 'Opened for Repair',
            'caretaker_requested': 'Caretaker Requested',
            'maintenance': 'Maintenance',
            'troubleshoot': 'Troubleshoot',
            'not_auto_updated': 'Had Not Been Auto Updated for a While',
            'get_realtime_readings': 'To Get Realtime Readings'
        };
        
        return reasonMap[rowData.reason] || rowData.reason;
    };

    return (
        <div className="card">
            <Toast ref={toast} />
            <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Communication Logs</h5>
                <div className="d-flex gap-2 align-items-center">
                    <div className="input-group" style={{ width: '300px' }}>
                        <span className="input-group-text">
                            <i className="ti ti-search" />
                        </span>
                        <InputText
                            className="form-control"
                            placeholder="Search logs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <Button
                                label="Clear"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setSearchTerm('')}
                            />
                        )}
                    </div>
                    <Button
                        label="Refresh"
                        className="btn btn-primary btn-sm"
                        onClick={fetchLogs}
                        loading={loading}
                    />
                </div>
            </div>
            <div className="card-body">
                <DataTable
                    value={filteredLogs}
                    paginator
                    rows={rows}
                    rowsPerPageOptions={rowsPerPageOptions}
                    stripedRows
                    loading={loading}
                    responsiveLayout="scroll"
                    emptyMessage="No communication logs found."
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    sortMode="multiple"
                    onPage={(e) => setRows(e.rows)}
                >
                    <Column 
                        field="command" 
                        header="Command" 
                        sortable 
                        style={{ minWidth: '150px' }}
                    />
                    <Column 
                        field="platform" 
                        header="Platform" 
                        sortable 
                        style={{ minWidth: '120px' }}
                    />
                    <Column 
                        field="actionBy" 
                        header="Action By" 
                        sortable 
                        body={actionByBodyTemplate}
                        style={{ minWidth: '150px' }}
                    />
                    <Column 
                        field="reason" 
                        header="Reason" 
                        sortable 
                        body={reasonBodyTemplate}
                        style={{ minWidth: '200px' }}
                    />
                    <Column 
                        header="Timestamp" 
                        body={timestampBodyTemplate} 
                        sortable 
                        sortField="timestamp"
                        style={{ minWidth: '180px' }}
                    />
                </DataTable>
            </div>
        </div>
    );
}

export default CommunicationLogsTab;